import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/api/auth';
import { api } from '@/lib/api';
import type { ExchangeItemInfo, ChildInfo } from '@/lib/api/types';
import { CATEGORY_LABELS, AVAILABLE_ICONS, COLOR_PRESETS, ICON_EMOJI_MAP } from '@/lib/api/rewardsTypes';
import { useCountdown } from '@/lib/context/CountdownContext';

interface StoreTabProps {
  items: ExchangeItemInfo[];
  children: ChildInfo[];
  onRefresh: () => void;
}

interface StoreForm {
  name: string;
  cost: string;
  category: string;
  description: string;
  icon: string;
  iconBg: string;
  enabled: boolean;
  hasCountdown: boolean;
  countdownMinutes: number;
}

interface RedeemState {
  itemId: string;
  childId: string;
}

// 分类映射：key -> 中文名称
const CATEGORY_KEY_TO_NAME: Record<string, string> = {
  companionship: '亲子陪伴',
  activity: '户外活动',
  toy: '玩具礼物',
  food: '美食奖励',
  privilege: '特权解锁',
  book: '知识成长'
};

// 分类映射：中文名称 -> key
const CATEGORY_NAME_TO_KEY: Record<string, string> = {
  '亲子陪伴': 'companionship',
  '户外活动': 'activity',
  '玩具礼物': 'toy',
  '美食奖励': 'food',
  '特权解锁': 'privilege',
  '知识成长': 'book'
};

export default function StoreTab({ items, children, onRefresh }: StoreTabProps) {
  const { currentFamily } = useAuth();
  const navigate = useNavigate();
  const { addCountdown } = useCountdown();
  const [activeCategory, setActiveCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);
  const [redeemState, setRedeemState] = useState<RedeemState | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<ExchangeItemInfo | null>(null);

  const isVip = currentFamily?.vip_status === 2;
  const canManage = currentFamily?.my_role === 1;
  const canAddItem = canManage && (isVip || items.length < 5);

  const [form, setForm] = useState<StoreForm>({
    name: '',
    cost: '',
    category: 'companionship',
    description: '',
    icon: 'gift',
    iconBg: '#3b82f6',
    enabled: true,
    hasCountdown: false,
    countdownMinutes: 30,
  });

  // 过滤时支持中文名称和英文key
  const filtered = items.filter(s => {
    if (activeCategory === 'all') return s.status === 1;
    // 检查category字段是否匹配英文key或中文名称
    const itemCategory = s.category || '';
    const matchByKey = itemCategory === activeCategory;
    const matchByName = itemCategory === CATEGORY_KEY_TO_NAME[activeCategory];
    return (matchByKey || matchByName) && s.status === 1;
  });

  const handleRedeem = async (itemId: string, childId: string) => {
    if (!currentFamily) return;
    const item = items.find(s => s.id === itemId);
    const child = children.find(c => c.id === childId);
    if (!item || !child) return;

    // Check if points are sufficient
    if (child.total_points < item.points_required) {
      alert(`积分不足！${child.nickname || child.name}当前积分：${child.total_points}，需要：${item.points_required}`);
      return;
    }

    setLoading(true);
    try {
      const res = await api.exchange.exchangePoints(currentFamily.id, {
        child_id: childId,
        item_id: itemId,
        source: 'PC',  // PC端来源
      });
      if (res.code === 0) {
        // 根据 exchange 返回的 timer_task 启动倒计时
        if (res.data?.timer_task) {
          const tt = res.data.timer_task;
          addCountdown({
            id: tt.id || `${itemId}-${Date.now()}`,
            childName: child.nickname || child.name,
            itemName: item.name,
            totalSeconds: tt.total_seconds || (item.timer_minutes ? item.timer_minutes * 60 : 0),
            remainingSeconds: tt.remaining_seconds || (item.timer_minutes ? item.timer_minutes * 60 : 0),
          });
        }
        alert(`兑换成功！${child.nickname || child.name}成功兑换了「${item.name}」`);
        setRedeemState(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Failed to redeem:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || '兑换失败，请稍后重试';
      alert(`兑换失败：${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveItem = async () => {
    if (!currentFamily) return;
    if (!form.name.trim()) {
      alert('请输入奖品名称');
      return;
    }
    if (!form.cost || parseFloat(form.cost) <= 0) {
      alert('请输入有效的积分值');
      return;
    }

    setLoading(true);
    try {
      const data: any = {
        name: form.name,
        points_required: Math.abs(parseFloat(form.cost)),
        category: form.category,
        icon: form.icon, // 直接存储emoji对应的value，与APP端一致
        icon_color: form.iconBg,
        description: form.description,
        status: form.enabled ? 1 : 0,
        timer_enabled: form.hasCountdown,
        timer_minutes: form.hasCountdown ? form.countdownMinutes : undefined,
      };

      if (editingItem) {
        await api.exchange.updateExchangeItem(currentFamily.id, editingItem.id, data);
      } else {
        await api.exchange.createExchangeItem(currentFamily.id, data);
      }

      setShowAddModal(false);
      setEditingItem(null);
      setForm({
        name: '',
        cost: '',
        category: 'companionship',
        description: '',
        icon: 'gift',
        iconBg: '#3b82f6',
        enabled: true,
        hasCountdown: false,
        countdownMinutes: 30,
      });
      onRefresh();
    } catch (error: any) {
      console.error('Failed to save item:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || '保存失败，请稍后重试';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item: ExchangeItemInfo) => {
    setEditingItem(item);
    // 兼容旧数据：如果icon是ri-开头的，提取value部分
    let iconValue = item.icon;
    if (iconValue.startsWith('ri-')) {
      // ri-gift-2-fill -> gift-2, ri-candy-fill -> candy
      iconValue = iconValue.replace('ri-', '').replace('-fill', '').replace('-line', '');
      // 映射回APP端的value
      const valueMap: Record<string, string> = {
        'gift-2': 'gift', 'candy': 'candy', 'movie': 'movie', 'gamepad': 'game',
        'book-3': 'book', 'football': 'sports', 'music-2': 'music', 'heart': 'toy',
        'cake-3': 'food', 'compass': 'travel', 'alarm': 'clock', 'camera': 'camera',
        'edit': 'pen', 'star': 'star', 'vip-crown': 'crown', 'trophy': 'trophy',
      };
      iconValue = valueMap[iconValue] || iconValue;
    }

    setForm({
      name: item.name,
      cost: item.points_required.toString(),
      category: item.category || 'companionship',
      description: item.description || '',
      icon: iconValue,
      iconBg: item.icon_color,
      enabled: item.status === 1,
      hasCountdown: item.timer_enabled || false,
      countdownMinutes: item.timer_minutes || 30,
    });
    setShowAddModal(true);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!currentFamily) return;
    if (!confirm('确定要删除该奖品吗？此操作不可撤销！')) return;

    setLoading(true);
    try {
      await api.exchange.deleteExchangeItem(currentFamily.id, itemId);
      onRefresh();
    } catch (error: any) {
      console.error('Failed to delete item:', error);
      alert('删除失败: ' + (error?.response?.data?.detail || error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex gap-1.5 flex-wrap">
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <button key={key} onClick={() => setActiveCategory(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer border whitespace-nowrap ${
                activeCategory === key ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400'
              }`}
            >{label}</button>
          ))}
        </div>
        <button onClick={() => {
          if (!canAddItem) {
            setShowVipModal(true);
            return;
          }
          setEditingItem(null);
          setShowAddModal(true);
        }}
          className={`ml-auto flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
            canAddItem
              ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:shadow-md'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}>
          <i className="ri-add-line"></i>新增奖品
          {!canAddItem && <span className="text-[10px]">({items.length}/5)</span>}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
        {filtered.map(item => (
          <div key={item.id} className="bg-white rounded-xl overflow-hidden hover:-translate-y-0.5 transition-all border border-blue-50">
            <div className="flex items-center gap-3 p-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 text-2xl" style={{ background: item.icon_color }}>
                {ICON_EMOJI_MAP[item.icon] || item.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-blue-900 text-xs mb-0.5 truncate">{item.name}</h4>
                <div className="flex items-center gap-1 mb-0.5">
                  <i className="ri-star-fill text-amber-400 text-sm"></i>
                  <span className="text-lg font-black" style={{ color: item.icon_color }}>{item.points_required}</span>
                  <span className="text-xs text-gray-400">积分</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-400">兑换{item.exchange_count}次</span>
                  {item.timer_enabled && item.timer_minutes && (
                    <span className="flex items-center gap-0.5 text-[10px] bg-cyan-50 text-cyan-600 px-1.5 py-0.5 rounded-full border border-cyan-100">
                      <i className="ri-timer-fill text-[9px]"></i>{item.timer_minutes}分钟
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex border-t border-blue-50">
              <button
                onClick={() => setRedeemState(redeemState?.itemId === item.id ? null : { itemId: item.id, childId: children[0]?.id || '' })}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
                style={{ background: item.icon_color }}
              >
                <i className="ri-exchange-fill text-xs"></i>兑换
              </button>
              <button onClick={() => {
                if (!canManage) { alert('记录员无权编辑奖品'); return; }
                handleEditItem(item);
              }}
                className={`flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium whitespace-nowrap border-l border-blue-50 transition-colors ${
                  canManage ? 'text-gray-500 bg-gray-50 hover:bg-gray-100 cursor-pointer' : 'text-gray-300 bg-gray-50 cursor-not-allowed'
                }`}>
                <i className="ri-edit-2-line text-xs"></i>编辑
              </button>
              <button onClick={() => {
                if (!canManage) { alert('记录员无权删除奖品'); return; }
                handleDeleteItem(item.id);
              }}
                className={`flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium whitespace-nowrap border-l border-blue-50 transition-colors ${
                  canManage ? 'text-red-500 hover:bg-red-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'
                }`}>
                <i className="ri-delete-bin-6-line text-xs"></i>删除
              </button>
            </div>
            {redeemState?.itemId === item.id && (
              <div className="px-3 pb-3 bg-blue-50 border-t border-blue-100">
                <p className="text-[10px] text-blue-700 mb-1.5 font-medium pt-2">为哪位孩子兑换？</p>
                <div className="flex gap-1.5">
                  {children.map(child => (
                    <button key={child.id} onClick={() => handleRedeem(item.id, child.id)}
                      disabled={loading}
                      className="flex-1 py-1.5 bg-blue-500 text-white text-[10px] rounded-lg hover:bg-blue-600 cursor-pointer transition-colors whitespace-nowrap font-medium disabled:opacity-50">
                      {child.nickname || child.name}
                    </button>
                  ))}
                </div>
                {item.timer_enabled && item.timer_minutes && (
                  <p className="text-[10px] text-cyan-600 mt-1.5 flex items-center gap-0.5">
                    <i className="ri-timer-fill"></i>兑换后将启动 {item.timer_minutes} 分钟倒计时
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Prize Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[500px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-6 pt-6 pb-4 border-b border-gray-50 flex items-center justify-between z-10">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className="ri-gift-2-fill text-amber-500"></i>
                {editingItem ? '编辑积分奖品' : '新增积分奖品'}
              </h3>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Preview */}
              <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0" style={{ background: form.iconBg }}>
                  <i className={`${form.icon.startsWith('ri-') ? form.icon : 'ri-' + form.icon} text-white text-2xl`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-blue-900 text-sm truncate">{form.name || '奖品名称预览'}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <i className="ri-star-fill text-amber-400 text-xs"></i>
                    <span className="text-xs font-bold text-amber-600">{form.cost || '0'} 积分</span>
                  </div>
                  {form.hasCountdown && (
                    <span className="flex items-center gap-0.5 text-[10px] bg-cyan-50 text-cyan-600 px-1.5 py-0.5 rounded-full border border-cyan-100 mt-1 w-fit">
                      <i className="ri-timer-fill text-[9px]"></i>{form.countdownMinutes}分钟倒计时
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">奖品名称</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="例如：玩游戲30分钟"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">选择图标</label>
                <div className="grid grid-cols-8 gap-2">
                  {AVAILABLE_ICONS.map(({ icon, emoji, label }) => (
                    <button key={icon} onClick={() => setForm({ ...form, icon })}
                      title={label}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl border-2 transition-all cursor-pointer text-lg ${
                        form.icon === icon ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">图标背景色</label>
                <div className="flex gap-2 flex-wrap">
                  {COLOR_PRESETS.map(color => (
                    <button key={color} onClick={() => setForm({ ...form, iconBg: color })}
                      className={`w-8 h-8 rounded-full cursor-pointer transition-transform hover:scale-110 ${form.iconBg === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''}`}
                      style={{ background: color }}></button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">兑换积分</label>
                  <input type="number" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} placeholder="例如：100"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">奖品分类</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400 bg-white">
                    {Object.entries(CATEGORY_LABELS).filter(([k]) => k !== 'all').map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Countdown toggle */}
              <div className="bg-cyan-50 rounded-xl border border-cyan-100 overflow-hidden">
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-timer-fill text-cyan-500 text-base"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">倒计时功能</p>
                      <p className="text-xs text-cyan-600">兑换后启动倒计时，时间到语音提醒</p>
                    </div>
                  </div>
                  <button onClick={() => setForm({ ...form, hasCountdown: !form.hasCountdown })}
                    className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${form.hasCountdown ? 'bg-cyan-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.hasCountdown ? 'left-6' : 'left-0.5'}`}></div>
                  </button>
                </div>
                {form.hasCountdown && (
                  <div className="px-4 pb-4 border-t border-cyan-100 pt-3">
                    <label className="text-xs font-semibold text-gray-600 mb-2 block">倒计时时长</label>
                    <div className="flex gap-2 flex-wrap">
                      {[10, 15, 20, 30, 45, 60, 90, 120].map(min => (
                        <button key={min} onClick={() => setForm({ ...form, countdownMinutes: min })}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                            form.countdownMinutes === min ? 'border-cyan-500 text-cyan-600 bg-cyan-50' : 'border-gray-200 text-gray-500'
                          }`}
                        >{min}分钟</button>
                      ))}
                    </div>
                    <p className="text-xs text-cyan-600 mt-2">
                      <i className="ri-information-fill mr-0.5"></i>
                      兑换后右下角显示倒计时浮窗，时间到语音提醒
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">奖品描述</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="描述这个奖品的详情..." rows={2} maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 resize-none" />
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <i className="ri-toggle-fill text-blue-500"></i>立即启用
                </span>
                <button onClick={() => setForm({ ...form, enabled: !form.enabled })}
                  className={`w-12 h-6 rounded-full transition-all cursor-pointer relative ${form.enabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.enabled ? 'left-6' : 'left-0.5'}`}></div>
                </button>
              </div>

              <button onClick={handleSaveItem}
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                确认{editingItem ? '编辑' : '添加'}奖品
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIP限制弹窗 */}
      {showVipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowVipModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[360px] p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 mx-auto mb-4">
              <i className="ri-vip-crown-fill text-white text-3xl"></i>
            </div>
            <h3 className="font-black text-blue-900 text-lg mb-2">免费版限制</h3>
            <p className="text-gray-500 text-sm mb-4">免费版最多添加 <strong>5个</strong> 兑换商品（当前{items.length}个），升级VIP可无限添加！</p>
            <button onClick={() => { setShowVipModal(false); navigate('/vip'); }} className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-white py-3 rounded-xl font-bold cursor-pointer whitespace-nowrap mb-2">
              立即升级 VIP
            </button>
            <button onClick={() => setShowVipModal(false)} className="text-gray-400 text-sm cursor-pointer">暂不升级</button>
          </div>
        </div>
      )}
    </div>
  );
}
